/**
 * Help-request message type constant.
 */
export const HELP_REQUEST_TYPE = 'help-request' as const;

/**
 * Payload structure for help-request messages.
 * Help requests contain a selectorType identifying which selector the user needs help with,
 * and optionally the options currently shown so the agent can reference them.
 */
export interface HelpRequestPayload {
    readonly type: typeof HELP_REQUEST_TYPE;
    readonly data: {
        readonly selectorType: string;
        readonly options?: ReadonlyArray<object>;
    };
}

/**
 * Build a help-request message payload as a JSON string.
 * The payload is serialized to JSON with the structure: {type: 'help-request', data: {selectorType, options?}}.
 *
 * @param selectorType - The type of selector the user needs help with (e.g., 'style-tiles', 'color-selector')
 * @param options - The options currently shown in the selector, so the agent can reference them
 * @returns JSON string representation of the help request payload
 */
export function buildHelpRequestPayload(
    selectorType: string,
    options?: ReadonlyArray<object>
): string {
    return JSON.stringify({
        type: HELP_REQUEST_TYPE,
        data: { selectorType, ...(options !== undefined ? { options } : {}) },
    });
}

/**
 * Check if a message content string is a valid help-request message.
 * Attempts to parse the content as JSON and validates the structure.
 *
 * @param content - The raw message content string to check
 * @returns true if content is valid help-request JSON, false otherwise
 */
export function isHelpRequestMessage(content: string): boolean {
    try {
        const parsed = JSON.parse(content);
        return (
            typeof parsed === 'object' &&
            parsed !== null &&
            parsed.type === HELP_REQUEST_TYPE &&
            typeof parsed.data === 'object' &&
            parsed.data !== null &&
            typeof parsed.data.selectorType === 'string'
        );
    } catch {
        return false;
    }
}

/**
 * Get the display text for help-request messages.
 * Used in UI to show what the help request action is labeled as.
 *
 * @returns Display text for help-request action
 */
export function getHelpRequestDisplayText(): string {
    return 'Help me answer';
}
